const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

type DecodedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
};

async function loadImageFromFile(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      // Fall back for browser decoders that cannot create an ImageBitmap.
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({
        source: img,
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        release: () => URL.revokeObjectURL(url),
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler a imagem."));
    };
    img.src = url;
  });
}

/** Downscale to the server's maximum image edge and JPEG quality. */
export async function optimizeAiStockImage(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;

  const image = await loadImageFromFile(file);
  try {
    const { width: w, height: h } = image;
    if (!w || !h) return file;

    const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
    const alreadySmallJpeg = scale >= 1 && file.type === "image/jpeg" && file.size <= 1_200_000;
    if (alreadySmallJpeg) return file;

    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = nw;
    canvas.height = nh;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(image.source, 0, 0, nw, nh);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    if (!blob || blob.size === 0) return file;
    if (blob.size >= file.size && scale >= 1) return file;

    const base = file.name.replace(/\.[^.]+$/, "") || "nota";
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    image.release();
  }
}

export function aiStockDevTime(label: string, startedAt: number, extra?: Record<string, unknown>) {
  if (!import.meta.env.DEV) return;
  const ms = Math.round(performance.now() - startedAt);
  // eslint-disable-next-line no-console
  console.info(`[ai-stock] ${label}: ${ms}ms`, extra ?? "");
}
